import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';

const ITEM_HEIGHT = 40;
const VISIBLE_ITEMS = 5;

function clampValue(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export interface CWheelItem {
  value: number;
  label: string;
}

export interface CWheelProps {
  items: CWheelItem[];
  selected: number;
  onChange: (value: number) => void;
  className?: string;
}

export function CWheel({ items, selected, onChange, className }: CWheelProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const dragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartOffset = useRef(0);
  const offsetRef = useRef(0);
  const [renderOffset, setRenderOffset] = useState(0);
  const currentVelocity = useRef(0);
  const lastPointerY = useRef(0);
  const lastPointerTime = useRef(0);

  const selectedIndex = useMemo(() => items.findIndex(item => item.value === selected), [items, selected]);

  const setOffset = useCallback((value: number) => {
    offsetRef.current = value;
    setRenderOffset(value);
  }, []);

  useEffect(() => {
    if (!dragging.current) {
      setOffset(-selectedIndex * ITEM_HEIGHT);
    }
  }, [selectedIndex, setOffset]);

  const snapToNearest = useCallback((offset: number) => {
    const maxOffset = 0;
    const minOffset = -(items.length - 1) * ITEM_HEIGHT;
    const clampedOffset = clampValue(offset, minOffset, maxOffset);
    const nearestIndex = Math.round(-clampedOffset / ITEM_HEIGHT);
    const safeIndex = clampValue(nearestIndex, 0, items.length - 1);
    const snappedOffset = -safeIndex * ITEM_HEIGHT;
    setOffset(snappedOffset);
    if (items[safeIndex].value !== selected) {
      onChange(items[safeIndex].value);
    }
  }, [items, selected, onChange, setOffset]);

  const animateDecay = useCallback((velocity: number, startOffset: number) => {
    const friction = 0.95;
    let currentVel = velocity;
    let offset = startOffset;

    const step = () => {
      currentVel *= friction;
      offset += currentVel;
      if (Math.abs(currentVel) < 0.5) {
        snapToNearest(offset);
        return;
      }
      setOffset(offset);
      animationRef.current = requestAnimationFrame(step);
    };
    animationRef.current = requestAnimationFrame(step);
  }, [snapToNearest, setOffset]);

  const handleDragStart = useCallback((clientY: number) => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    dragging.current = true;
    dragStartY.current = clientY;
    dragStartOffset.current = offsetRef.current;
    lastPointerY.current = clientY;
    lastPointerTime.current = Date.now();
    currentVelocity.current = 0;
  }, []);

  const handleDragMove = useCallback((clientY: number) => {
    if (!dragging.current) return;
    const delta = clientY - dragStartY.current;
    const now = Date.now();
    const timeDelta = now - lastPointerTime.current;
    if (timeDelta > 0) {
      currentVelocity.current = (clientY - lastPointerY.current) / timeDelta * 16;
    }
    lastPointerY.current = clientY;
    lastPointerTime.current = now;
    setOffset(dragStartOffset.current + delta);
  }, [setOffset]);

  const handleDragEnd = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    if (Math.abs(currentVelocity.current) > 2) {
      animateDecay(currentVelocity.current, offsetRef.current);
    } else {
      snapToNearest(offsetRef.current);
    }
  }, [animateDecay, snapToNearest]);

  const handleWheel = useCallback((event: WheelEvent) => {
    event.preventDefault();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    const direction = event.deltaY > 0 ? -1 : 1;
    const newOffset = offsetRef.current + direction * ITEM_HEIGHT;
    const maxOffset = 0;
    const minOffset = -(items.length - 1) * ITEM_HEIGHT;
    const clampedOffset = clampValue(newOffset, minOffset, maxOffset);
    setOffset(clampedOffset);
    snapToNearest(clampedOffset);
  }, [items.length, snapToNearest, setOffset]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const onTouchStart = (event: TouchEvent) => { event.preventDefault(); handleDragStart(event.touches[0].clientY); };
    const onTouchMove = (event: TouchEvent) => { event.preventDefault(); handleDragMove(event.touches[0].clientY); };
    const onTouchEnd = () => handleDragEnd();
    const onMouseDown = (event: MouseEvent) => { event.preventDefault(); handleDragStart(event.clientY); };
    const onMouseMove = (event: MouseEvent) => handleDragMove(event.clientY);
    const onMouseUp = () => handleDragEnd();

    element.addEventListener('touchstart', onTouchStart, { passive: false });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd);
    element.addEventListener('mousedown', onMouseDown);
    element.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('mousedown', onMouseDown);
      element.removeEventListener('wheel', handleWheel);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [handleDragStart, handleDragMove, handleDragEnd, handleWheel]);

  const centerOffset = Math.floor(VISIBLE_ITEMS / 2) * ITEM_HEIGHT;

  return (
    <div className={classNames('c-wheel', className)} ref={containerRef}>
      <div className="c-wheel__highlight" />
      <div
        className="c-wheel__items"
        style={{ transform: `translateY(${renderOffset + centerOffset}px)` }}
      >
        {items.map((item, index) => {
          const distanceFromCenter = Math.abs(renderOffset + index * ITEM_HEIGHT) / ITEM_HEIGHT;
          const opacity = Math.max(0.3, 1 - distanceFromCenter * 0.25);
          const scale = Math.max(0.85, 1 - distanceFromCenter * 0.05);
          return (
            <div
              key={item.value}
              className={classNames('c-wheel__item', {
                'c-wheel__item--selected': item.value === selected,
              })}
              style={{ opacity, transform: `scale(${scale})` }}
            >
              {item.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}
